import type { ReactElement } from 'react';
import { Code2, Webhook } from 'lucide-react';
import type { IntegrationProvider } from '@wrapforce/shared';

type LogoKey = IntegrationProvider | 'stripe' | 'quote_widget';

function StripeMark() {
  return (
    <svg viewBox="0 0 32 32" className="h-5 w-5" aria-hidden="true">
      <path
        fill="#635BFF"
        d="M13.3 12.2c0-1.1.9-1.5 2.3-1.5 2 0 4.6.6 6.6 1.7V5.9c-2.2-.9-4.4-1.2-6.6-1.2-5.4 0-9 2.8-9 7.5 0 7.3 10 6.1 10 9.3 0 1.3-1.1 1.7-2.6 1.7-2.2 0-5-.9-7.2-2.1v6.6c2.5 1.1 5 1.5 7.2 1.5 5.5 0 9.3-2.7 9.3-7.4 0-7.9-10-6.6-10-9.6z"
      />
    </svg>
  );
}

function TwilioMark() {
  return (
    <svg viewBox="0 0 24 24" className="h-5 w-5" aria-hidden="true">
      <circle cx="12" cy="12" r="9.5" fill="none" stroke="#F22F46" strokeWidth="3.4" />
      <circle cx="9" cy="9" r="1.9" fill="#F22F46" />
      <circle cx="15" cy="9" r="1.9" fill="#F22F46" />
      <circle cx="9" cy="15" r="1.9" fill="#F22F46" />
      <circle cx="15" cy="15" r="1.9" fill="#F22F46" />
    </svg>
  );
}

function QuickBooksMark() {
  return (
    <svg viewBox="0 0 24 24" className="h-5 w-5" aria-hidden="true">
      <circle cx="12" cy="12" r="11" fill="#2CA01C" />
      <path
        fill="none"
        stroke="#fff"
        strokeWidth="1.9"
        d="M9.4 7.6a4.4 4.4 0 0 0 0 8.8h1.1M9.9 5.4v11M14.6 16.4a4.4 4.4 0 0 0 0-8.8h-1.1M14.1 18.6v-11"
      />
    </svg>
  );
}

function GoogleGMark() {
  return (
    <svg viewBox="0 0 24 24" className="h-5 w-5" aria-hidden="true">
      <path
        fill="#4285F4"
        d="M23.49 12.27c0-.79-.07-1.54-.19-2.27H12v4.51h6.47c-.29 1.48-1.14 2.73-2.4 3.58v3h3.86c2.26-2.09 3.56-5.17 3.56-8.82z"
      />
      <path
        fill="#34A853"
        d="M12 24c3.24 0 5.95-1.08 7.93-2.91l-3.86-3c-1.08.72-2.45 1.16-4.07 1.16-3.13 0-5.78-2.11-6.73-4.96H1.29v3.09C3.26 21.3 7.31 24 12 24z"
      />
      <path fill="#FBBC05" d="M5.27 14.29A7.2 7.2 0 0 1 4.89 12c0-.8.14-1.57.38-2.29V6.62H1.29a12 12 0 0 0 0 10.76l3.98-3.09z" />
      <path
        fill="#EA4335"
        d="M12 4.75c1.77 0 3.35.61 4.6 1.8l3.42-3.42C17.95 1.19 15.24 0 12 0 7.31 0 3.26 2.7 1.29 6.62l3.98 3.09c.95-2.85 3.6-4.96 6.73-4.96z"
      />
    </svg>
  );
}

function GoogleCalendarMark() {
  return (
    <svg viewBox="0 0 24 24" className="h-5 w-5" aria-hidden="true">
      <rect x="3" y="3" width="18" height="18" fill="#fff" stroke="#4285F4" strokeWidth="2" />
      <rect x="3" y="3" width="18" height="4.5" fill="#4285F4" />
      <text x="12" y="17.5" textAnchor="middle" fontSize="9" fontWeight="700" fontFamily="Arial, sans-serif" fill="#4285F4">
        31
      </text>
    </svg>
  );
}

function SquareMark() {
  return (
    <svg viewBox="0 0 24 24" className="h-5 w-5" aria-hidden="true">
      <path
        fill="#000"
        fillRule="evenodd"
        d="M7 3h10a4 4 0 0 1 4 4v10a4 4 0 0 1-4 4H7a4 4 0 0 1-4-4V7a4 4 0 0 1 4-4zm1 5a1 1 0 0 0-1 1v6a1 1 0 0 0 1 1h8a1 1 0 0 0 1-1V9a1 1 0 0 0-1-1H8z"
      />
      <rect x="9.8" y="9.8" width="4.4" height="4.4" fill="#000" />
    </svg>
  );
}

const MARKS: Record<LogoKey, () => ReactElement> = {
  stripe: StripeMark,
  quote_widget: () => <Code2 size={20} className="text-accent" aria-hidden="true" />,
  twilio: TwilioMark,
  quickbooks: QuickBooksMark,
  google_calendar: GoogleCalendarMark,
  google_reviews: GoogleGMark,
  webhooks: () => <Webhook size={20} className="text-ink" aria-hidden="true" />,
  square: SquareMark,
};

export function ProviderLogo({ provider }: { provider: LogoKey }) {
  const Mark = MARKS[provider];
  return (
    <span className="flex h-9 w-9 shrink-0 items-center justify-center border border-border bg-white">
      <Mark />
    </span>
  );
}
